import type { Severity } from './severityTones';
import type { StoreIssue } from './components/stores/StoreIssuesPanel';

export interface AttentionStoreInput {
  storeId: string;
  storeName: string;
  /**
   * The store's issues, exactly as `buildStoreViewModels` computed them. The
   * queue does not re-derive store problems — a store is in the queue if and
   * only if `storeNeedsAttention` is true for it, so the queue and the
   * "N need attention" tile always count the same stores.
   */
  issues: StoreIssue[];
}

export interface AttentionJobInput {
  status: string;
  createdAt?: string | null;
  completedAt?: string | null;
}

export interface AttentionRebateInput {
  subject?: string;
  from?: string;
  recordsSaved?: number;
  recordsParsed?: number;
  status?: string;
}

/** A store's event rebate request to this brand, awaiting the vendor's decision (flag `event_rebate_requests`). */
export interface AttentionRebateRequestInput {
  status: string;
}

export interface AttentionItem {
  id: string;
  severity: Severity;
  title: string;
  meta: string;
  sourceLabel: string;
  actionLabel?: string;
  actionHref?: string;
}

export interface DeriveAttentionItemsInput {
  stores: AttentionStoreInput[];
  jobs: AttentionJobInput[];
  rebates: AttentionRebateInput[];
  /** Event rebate requests to this brand; only `pending` ones raise an item. Omit while the flag is off. */
  rebateRequests?: AttentionRebateRequestInput[];
}

export interface DeriveAttentionItemsResult {
  items: AttentionItem[];
  truncatedCount: number;
}

const SEVERITY_ORDER: Record<Severity, number> = { blocking: 0, warning: 1, info: 2, positive: 3 };
const MAX_ITEMS = 6;
const FAILED_JOB_WINDOW_MS = 24 * 60 * 60 * 1000;
const FAILED_JOB_THRESHOLD = 2;

export function deriveAttentionItems(input: DeriveAttentionItemsInput): DeriveAttentionItemsResult {
  const { stores, jobs, rebates, rebateRequests = [] } = input;
  const items: AttentionItem[] = [];
  const now = Date.now();

  // One item per store that needs attention, led by its worst issue.
  for (const store of stores) {
    if (store.issues.length === 0) continue;
    const ordered = [...store.issues].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
    const [worst, ...rest] = ordered;
    items.push({
      id: `store-${store.storeId}`,
      severity: worst.severity,
      title: `${store.storeName}: ${worst.title}`,
      meta:
        rest.length > 0
          ? `${worst.meta} Also: ${rest.map((issue) => issue.title).join('; ')}.`
          : worst.meta,
      sourceLabel: store.storeName,
    });
  }

  const recentFailedJobs = jobs.filter((job) => {
    if (job.status !== 'failed') return false;
    const at = job.completedAt || job.createdAt;
    if (!at) return false;
    return now - new Date(at).getTime() <= FAILED_JOB_WINDOW_MS;
  });
  if (recentFailedJobs.length >= FAILED_JOB_THRESHOLD) {
    items.push({
      id: 'feed-failures',
      severity: 'blocking',
      title: `${recentFailedJobs.length} sync jobs failed in the last 24 hours`,
      meta: 'Your ingestion feed has failed more than once today.',
      sourceLabel: 'Ingestion feed',
    });
  }

  rebates.forEach((rebate, index) => {
    // recordsSaved only counts rows actually created/updated — a clean
    // re-import of already-current data can legitimately have
    // recordsSaved < recordsParsed while the ingestion service still records
    // status: 'success'. status is the authoritative partial/error signal.
    if (rebate.status === 'partial') {
      const saved = rebate.recordsSaved ?? 0;
      const parsed = rebate.recordsParsed ?? 0;
      items.push({
        id: `rebate-partial-${index}`,
        severity: 'warning',
        title: `${rebate.subject || 'A rebate file'} only partially processed`,
        meta: `${saved} of ${parsed} rows saved.`,
        sourceLabel: 'Rebates',
      });
    } else if (rebate.status === 'error') {
      items.push({
        id: `rebate-error-${index}`,
        severity: 'blocking',
        title: `${rebate.subject || 'A rebate file'} failed to process`,
        meta: 'Nothing from this file was saved.',
        sourceLabel: 'Rebates',
      });
    }
  });

  const pendingRequests = rebateRequests.filter((r) => r.status === 'pending').length;
  if (pendingRequests > 0) {
    items.push({
      id: 'rebate-requests-pending',
      severity: 'warning',
      title: `${pendingRequests} event rebate request${pendingRequests === 1 ? ' is' : 's are'} waiting for your decision`,
      meta: 'Stores asked you to approve an event or anniversary rebate.',
      sourceLabel: 'Rebate requests',
      actionLabel: 'Review',
      actionHref: '/app/vendor-rebate-management?tab=requests',
    });
  }

  items.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  return {
    items: items.slice(0, MAX_ITEMS),
    truncatedCount: Math.max(0, items.length - MAX_ITEMS),
  };
}
